export type BannerStatus = "scheduled" | "active" | "finished" | "inactive";
export type BannerCtaType = "external_url" | "internal_page" | "internal_route" | "embedded_pdf";
export type BannerActionButtonType = "external_url" | "internal_route";
export type BannerAppearanceColor =
  | "#339966"
  | "#CCCCFF"
  | "#3366FF"
  | "#0033FF"
  | "#000000"
  | "#333333";

export const MAX_ACTIVE_BANNERS = 4;
export const BANNER_SHORT_TEXT_MAX_LENGTH = 150;
export const BANNER_IMAGE_MAX_SIZE_KB = 500;
/** Recorte 16:9 del hero de la landing, que la app dibuja con esa proporción. */
export const BANNER_IMAGE_CROP_WIDTH = 1280;
export const BANNER_IMAGE_CROP_HEIGHT = 720;

/**
 * Mínimo del banner del carrusel. La app lo dibuja como una franja
 * (`width: 100%`, `height: 64`, `cover`), o sea ~5.7:1, así que no se fuerza
 * una proporción: se exige resolución suficiente y el recorte lo hace el
 * cliente. Tiene que coincidir con `BANNER_IMAGE_MIN_*` del worker.
 */
export const BANNER_IMAGE_MIN_WIDTH = 1080;
export const BANNER_IMAGE_MIN_HEIGHT = 190;

/** BN-010: limites de la landing. Los mismos que valida BN-020 del lado de la API. */
export const BANNER_LANDING_TITLE_MAX_LENGTH = 100;
/** Se cuenta sobre el TEXTO VISIBLE, no sobre el HTML: ver `bannerLandingBodyLength`. */
export const BANNER_LANDING_BODY_MAX_LENGTH = 1000;
export const BANNER_LANDING_CALLOUT_MAX_LENGTH = 200;
export const BANNER_LANDING_BUTTON_TEXT_MAX_LENGTH = 40;
/** Ultimos N caracteres: el contador pasa a warning antes de tocar el tope. */
export const BANNER_LANDING_COUNTER_WARNING_MARGIN = 10;

export const BANNER_APPEARANCE_COLORS: BannerAppearanceColor[] = [
  "#339966",
  "#CCCCFF",
  "#3366FF",
  "#0033FF",
  "#000000",
  "#333333",
];

/**
 * BN-009 dejaba "Página Interna" visible y deshabilitada porque el editor de landing era
 * Sprint 2. BN-010 entrega ese editor, así que la opción queda habilitada: es el último paso
 * del ticket ("quitar el estado deshabilitado").
 */
export const BANNER_CTA_TYPE_OPTIONS: { label: string; value: BannerCtaType }[] = [
  { label: "URL Externa", value: "external_url" },
  { label: "Página Interna", value: "internal_page" },
  { label: "Ruta Interna", value: "internal_route" },
  { label: "PDF Incrustado", value: "embedded_pdf" },
];

export const BANNER_ACTION_BUTTON_TYPE_OPTIONS: {
  label: string;
  value: BannerActionButtonType;
}[] = [
  { label: "URL Externa", value: "external_url" },
  { label: "Ruta Interna", value: "internal_route" },
];

export const BANNER_INTERNAL_ROUTE_OPTIONS: { label: string; value: string }[] = [
  { label: "Home", value: "/" },
  { label: "Comunicaciones institucionales", value: "/comunicaciones-institucionales" },
  { label: "Beneficios", value: "/beneficios" },
];

/**
 * Color del texto del botón de acción de la landing, por color de fondo elegido.
 *
 * Es una TABLA FIJA, no un cálculo en runtime: el criterio de aceptación dice "texto del botón
 * según lookup, sin excepción". Cada entrada es la opción (blanco o negro) que maximiza el
 * contraste contra ese fondo, y el número al lado es la relación resultante segun WCAG 2.1: la
 * spec exige AA 4.5:1 para texto normal sobre el botón, y las seis entradas lo cumplen.
 *
 * OJO con `#339966`: es el color de marca y en el resto del Backoffice lleva texto blanco
 * (SYSTEM_DESIGN §6.3), pero blanco sobre ese verde da 3.57:1 y NO pasa AA. Acá manda la regla
 * de accesibilidad de la spec de la landing, así que va negro. Si Producto prefiere blanco por
 * consistencia de marca, es un cambio de UNA línea de esta tabla — y una excepción consciente
 * a AA, no un descuido.
 */
export const BANNER_APPEARANCE_TEXT_COLOR: Record<BannerAppearanceColor, "#FFFFFF" | "#000000"> = {
  "#339966": "#000000", // 5.89:1  (blanco daria 3.57:1, no pasa AA)
  "#CCCCFF": "#000000", // 13.65:1
  "#3366FF": "#FFFFFF", // 4.68:1
  "#0033FF": "#FFFFFF", // 7.20:1
  "#000000": "#FFFFFF", // 21.00:1
  "#333333": "#FFFFFF", // 12.63:1
};

/** Fondo tint del callout: el color de acento al 10% (spec 2.5 / 8-10%). */
export const BANNER_CALLOUT_TINT_ALPHA = 0.1;

export const BANNER_STATUS_OPTIONS: { label: string; value: BannerStatus }[] = [
  { label: "Activo", value: "active" },
  { label: "Inactivo", value: "inactive" },
  { label: "Programado", value: "scheduled" },
  { label: "Finalizado", value: "finished" },
];

export interface BannerLandingPage {
  heroImageUrl: string | null;
  title: string;
  body: string | null;
  importantMessage: string | null;
  appearanceColor: BannerAppearanceColor;
  actionButtonEnabled: boolean;
  actionButtonText: string | null;
  actionButtonType: BannerActionButtonType | null;
  actionButtonValue: string | null;
}

export interface Banner {
  id: string;
  name: string;
  shortText: string | null;
  imageUrl: string;
  imageWidth: number | null;
  imageHeight: number | null;
  imageSizeKb: number | null;
  ctaType: BannerCtaType;
  ctaValue: string | null;
  status: BannerStatus;
  sectionKey: string;
  startDate: string | null;
  endDate: string | null;
  sortOrder: number;
  landingPage: BannerLandingPage | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateBannerPayload {
  name: string;
  shortText: string | null;
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  imageSizeKb: number;
  ctaType: BannerCtaType;
  ctaValue: string | null;
  status: BannerStatus;
  startDate: string | null;
  endDate: string | null;
  /** Ausente: el backend asigna el siguiente libre de la sección. */
  sortOrder?: number;
  landingPage: BannerLandingPage | null;
}

export type UpdateBannerPayload = Partial<CreateBannerPayload>;
